import { useId, useState } from 'react';
import type { DraftReview } from '../api/types';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { tryBlockedText } from '../app/status';

export interface TryButtonProps {
  review: DraftReview | null;
  /** 押したときの配備。無ければ常に押せない（Draft が無い・fixture モード）。 */
  onTry?(confirmReplace: boolean): void;
  /** 配備の受付中。 */
  busy?: boolean;
}

/**
 * app の見出しの「試す」（Claude Design の app header）。「試す」は現行パッケージの置換なので（要件 D1）、
 * 現行のパッケージがある app では確認を挟む（公開していなくても ZIP 登録で動いていることがある。C2）。
 * 止めている理由（エラー → 未検証 → 配備中）はボタンの説明にする（D15・G9）。
 */
export function TryButton({ review, onTry, busy = false }: TryButtonProps) {
  const reasonId = useId();
  const reason = tryBlockedText(review);
  const [confirming, setConfirming] = useState(false);
  const publication = review?.publication ?? null;
  // 公開が無くても ZIP 登録で動いていれば置き換えになる。自分の試運転の配備（trial）の作り直しは挟まない（C2・U8）。
  const current = review?.currentPackage ?? 'none';
  const replacing = current === 'published' || current === 'external';

  return (
    <div className="try-row">
      {reason !== null && (
        <span id={reasonId} className="try-reason">
          {reason}
        </span>
      )}
      <Button
        disabled={onTry === undefined || reason !== null || busy}
        aria-describedby={reason !== null ? reasonId : undefined}
        onClick={() => {
          if (onTry === undefined) return;
          // 現行のパッケージがあれば「現行を置き換えます」を見せてから配備する。
          if (replacing) setConfirming(true);
          else onTry(false);
        }}
      >
        {busy ? '配備中…' : '試す'}
      </Button>
      {confirming && replacing && (
        <ConfirmDialog
          title="現行を置き換えます"
          body={
            <p>
              {publication !== null
                ? `公開中 v${publication.version} で動いている試運転はありません。下書きで試運転すると、次の試運転まで現行の動きは確認できません。`
                : '現行のパッケージを置き換えます。下書きで試運転すると、もう一度配備するまで現行の動きは確認できません。'}
            </p>
          }
          confirmLabel="下書きで試す"
          onConfirm={() => {
            setConfirming(false);
            onTry?.(true);
            return Promise.resolve();
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
